import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LANDING } from "@/constants/testIds";
import { BUNDLES, GALLERY_IMAGES } from "@/data/landing";
import {
  HEIGHTS,
  COLORS,
  HIGH_HEIGHT_UPCHARGE,
  getStoredHeight,
  setStoredHeight,
  getStoredColor,
  setStoredColor,
} from "@/hooks/usePillowOptions";
import ShippingProgressBar from "@/components/ShippingProgressBar";
import ShippingTrustBlock from "@/components/ShippingTrustBlock";
import ReviewsRatingStrip from "@/components/ReviewsRatingStrip";
import TrustBadgesStrip from "@/components/TrustBadgesStrip";
import LaunchPromoExplainer from "@/components/LaunchPromoExplainer";
import PreCheckoutEmailModal from "@/components/PreCheckoutEmailModal";
import GiftMaskSelector from "@/components/GiftMaskSelector";
import { Shield, Globe, RotateCcw, Leaf, Tag, X, Check } from "lucide-react";
import { track } from "@/lib/pixel";
import { mpTrack, newEventId, setCheckoutEventId } from "@/lib/metaPixel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GIFT_MASKS_BY_BUNDLE = { 1: 0, 2: 1, 4: 2 };

export default function ShopZone() {
  const [qty, setQty] = useState(1);
  const [height, setHeight] = useState(() => getStoredHeight());
  const [color, setColor] = useState(() => getStoredColor());
  const [activeImg, setActiveImg] = useState(0);
  const [mainMode, setMainMode] = useState("color"); // "color" | "gallery"
  const [loading, setLoading] = useState(false);
  const [bumpExtra, setBumpExtra] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [maskColors, setMaskColors] = useState(["Black", "Black"]); // up to 2 slots
  const [discountCode, setDiscountCode] = useState(() => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("dp_discount");
  });
  const [couponInput, setCouponInput] = useState("");
  const [couponError, setCouponError] = useState("");

  // Sync with other discount entry points (ExitPopup, SleepQuiz, FloatingCouponBanner)
  useEffect(() => {
    const sync = () => setDiscountCode(localStorage.getItem("dp_discount"));
    window.addEventListener("storage", sync);
    window.addEventListener("dp:discount-changed", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("dp:discount-changed", sync);
    };
  }, []);

  const VALID_COUPONS = { DUBAI10: 0.10, DUBAI15: 0.15, TEST99: 0.99 };

  const applyCoupon = () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) {
      setCouponError("Enter a code");
      return;
    }
    if (!(code in VALID_COUPONS)) {
      setCouponError("Invalid code — try DUBAI10 or DUBAI15");
      track("coupon_invalid", { code });
      return;
    }
    localStorage.setItem("dp_discount", code);
    setDiscountCode(code);
    setCouponInput("");
    setCouponError("");
    track("coupon_applied", { code });
    toast.success(`${code} applied · ${Math.round(VALID_COUPONS[code] * 100)}% off`);
    window.dispatchEvent(new Event("dp:discount-changed"));
  };

  const removeCoupon = () => {
    localStorage.removeItem("dp_discount");
    setDiscountCode(null);
    setCouponError("");
    track("coupon_removed");
    window.dispatchEvent(new Event("dp:discount-changed"));
  };

  const updateHeight = (h) => {
    setHeight(h);
    setStoredHeight(h);
    track("select_height", { height: h });
  };
  const updateColor = (c) => {
    setColor(c);
    setStoredColor(c);
    setMainMode("color");
    track("select_color", { color: c });
  };
  const selectThumb = (i) => {
    setActiveImg(i);
    setMainMode("gallery");
    track("select_thumb", { index: i });
  };
  const updateQty = (q) => {
    setQty(q);
    track("select_bundle", { qty: q });
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("dp:qty-change", { detail: q }));
    }
  };

  const updateMaskColor = (slot, c) => {
    setMaskColors((prev) => {
      const next = [...prev];
      next[slot] = c;
      return next;
    });
    track("select_mask_color", { slot, color: c });
  };

  const giftMaskCount = GIFT_MASKS_BY_BUNDLE[qty] || 0;

  const selected = useMemo(() => BUNDLES.find((b) => b.qty === qty), [qty]);
  const highUpcharge = height === "High" ? HIGH_HEIGHT_UPCHARGE * selected.qty : 0;
  const basePillowPrice = selected.price + highUpcharge;
  const BUMP_BASE = 69;
  const bumpCharge = bumpExtra ? BUMP_BASE + (height === "High" ? HIGH_HEIGHT_UPCHARGE : 0) : 0;
  const displayedPrice = basePillowPrice + bumpCharge;
  const displayedOriginal = selected.original + highUpcharge + (bumpExtra ? 199 + (height === "High" ? HIGH_HEIGHT_UPCHARGE : 0) : 0);
  const displayedSavings = displayedOriginal - displayedPrice;
  const totalPillows = selected.qty + (bumpExtra ? 1 : 0);
  const perPillow = (displayedPrice / totalPillows).toFixed(2);
  const discountPct = Math.round((displayedSavings / displayedOriginal) * 100);
  const couponPct = discountCode && VALID_COUPONS[discountCode] ? VALID_COUPONS[discountCode] : 0;
  const finalPrice = couponPct > 0 ? +(displayedPrice * (1 - couponPct)).toFixed(2) : displayedPrice;
  const hasDiscount = couponPct > 0;

  const startCheckout = () => {
    track("click_buy_now", { qty, height, color, bump_extra: bumpExtra, has_discount: hasDiscount });
    // Meta Pixel: AddToCart fires when the user commits to a bundle
    // (no traditional "cart" exists — clicking Buy is the add-to-cart moment).
    mpTrack("AddToCart", {
      content_ids: [`DP-CLASSIC-${qty}`],
      content_name: `Dubai Pillow Bundle (${qty}×)`,
      content_type: "product",
      contents: [{ id: `DP-CLASSIC-${qty}`, quantity: qty, item_price: finalPrice / qty }],
      value: finalPrice,
      currency: "USD",
    });
    setEmailModalOpen(true);
  };

  const continueToStripe = async (email, name) => {
    try {
      const giftMaskCount = GIFT_MASKS_BY_BUNDLE[qty] || 0;
      const masksPayload = giftMaskCount > 0 ? maskColors.slice(0, giftMaskCount) : undefined;

      // 1. Create intent for abandoned cart tracking
      const { data: intent } = await axios.post(`${API}/checkout/intent`, {
        email,
        name: name || undefined,
        qty,
        height,
        color,
        bump_extra: bumpExtra,
        discount_code: discountCode || undefined,
        mask_colors: masksPayload,
      });
      localStorage.setItem("dp_intent_id", intent.intent_id);
      track("intent_created", { qty, height, color, masks: masksPayload });

      // Meta Pixel: generate a shared event_id so server-side CAPI Purchase
      // (fired from Stripe webhook) can dedupe with the browser Purchase event.
      const metaEventId = newEventId();
      setCheckoutEventId(metaEventId);
      mpTrack("InitiateCheckout", {
        content_ids: [`DP-CLASSIC-${qty}`],
        content_type: "product",
        contents: [{ id: `DP-CLASSIC-${qty}`, quantity: qty }],
        num_items: qty + (bumpExtra ? 1 : 0),
        value: finalPrice,
        currency: "USD",
      }, metaEventId);

      // 2. Create Stripe session linked to the intent
      const { data } = await axios.post(`${API}/checkout/session`, {
        qty,
        height,
        color,
        origin_url: window.location.origin,
        discount_code: discountCode || undefined,
        bump_extra: bumpExtra,
        intent_id: intent.intent_id,
        mask_colors: masksPayload,
        meta_event_id: metaEventId,
      });
      if (data?.url) {
        track("redirect_to_stripe", { intent_id: intent.intent_id });
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed. Please try again.");
      throw e;
    }
  };

  const checkout = startCheckout;  // legacy alias for the CTA

  return (
    <section
      id="shop"
      data-testid={LANDING.shopZone}
      className="aurora-wrap py-20 md:py-28 scroll-mt-24"
    >
      <div className="container-x">
        <div className="text-center mb-12 md:mb-16">
          <span className="section-pill">🛒 Shop</span>
          <h2 className="mt-5 text-[34px] sm:text-[44px] md:text-[56px] leading-[1.05] font-black tracking-[-0.02em] text-[#0e0e10]">
            Build your bundle.
            <br />
            <span className="text-[#8a8a8a]">Save up to 63%.</span>
          </h2>
          <p className="mt-4 text-[#5a5a60] text-[15px] max-w-xl mx-auto">
            Why buy 1 when 2 saves you $211 (53% off) and 4 saves you{" "}
            <strong className="text-[#0e0e10]">$499 (63% off)</strong>?
            Most customers order for themselves + their partner.
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_440px] gap-10 lg:gap-14 items-start max-w-6xl mx-auto">
          {/* GALLERY */}
          <div>
            <div className="aspect-square bg-white rounded-[32px] overflow-hidden border border-black/5 shadow-sm relative">
              <img
                key={`${mainMode}-${mainMode === "color" ? color : activeImg}`}
                data-testid={LANDING.galleryMain}
                src={
                  mainMode === "gallery"
                    ? GALLERY_IMAGES[activeImg].src
                    : COLORS.find((c) => c.key === color)?.image ||
                      GALLERY_IMAGES[activeImg].src
                }
                alt={`Dubai Pillow · ${color}`}
                className="w-full h-full object-cover fade-in-up"
              />
              <div className="sticker sticker-lime absolute top-5 left-5 text-[12px]">
                Save up to $499
              </div>
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between pointer-events-none">
                <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-[#0e0e10] bg-white/90 backdrop-blur px-2.5 py-1 rounded-full">
                  {color}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] font-extrabold text-white bg-[#0e0e10]/85 backdrop-blur px-2.5 py-1 rounded-full">
                  {height} · {height === "Medium" ? "15cm" : "18cm"}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-4 md:grid-cols-5 gap-2 mt-3">
              {GALLERY_IMAGES.map((img, i) => (
                <button
                  key={i}
                  data-testid={LANDING.galleryThumb(i)}
                  onClick={() => selectThumb(i)}
                  className={`aspect-square overflow-hidden rounded-2xl bg-white border-2 transition ${
                    mainMode === "gallery" && activeImg === i ? "border-[#0e0e10]" : "border-transparent hover:border-black/20"
                  }`}
                >
                  <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* BUY PANEL */}
          <div className="bg-white rounded-[32px] border border-black/5 shadow-lg p-6 md:p-8">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-[#22c55e] font-bold">
              <span className="w-2 h-2 rounded-full bg-[#22c55e] pulse-dot" />
              In stock · ships in 24h
            </div>
            <h3 className="mt-3 text-[24px] md:text-[28px] font-black text-[#0e0e10] tracking-tight leading-tight">
              Dubai Pillow — 7-Star Edition
            </h3>

            <div className="mt-3 flex items-baseline gap-2.5">
              <span className="text-[42px] md:text-[52px] font-black text-[#0e0e10] tabular-nums leading-none">
                ${perPillow}
              </span>
              <span className="text-[14px] md:text-[15px] font-bold text-[#5a5a60] uppercase tracking-[0.08em]">
                / pillow
              </span>
              {displayedSavings > 0 && (
                <span className="bg-[#d8f06b] text-[#0e0e10] text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider self-center">
                  Save ${displayedSavings} · {discountPct}% OFF
                </span>
              )}
            </div>
            <div className="mt-1.5 text-[12px] text-[#8a8a8a] tabular-nums" data-testid="bundle-total-line">
              × {totalPillows} {totalPillows === 1 ? "pillow" : "pillows"} ={" "}
              <span className="font-bold text-[#3a3a40]">${displayedPrice}</span>
              <span className="text-[#aaa] line-through ml-1.5">${displayedOriginal}</span>
            </div>
            <div className="text-[12px] text-[#8a8a8a] mt-1">
              74 × 48 cm · {selected.perks}
              {height === "High" && (
                <span className="ml-1 text-[#b8952a] font-semibold">· High loft +$10/pillow</span>
              )}
              {bumpExtra && (
                <span className="ml-1 text-[#22c55e] font-semibold">· +1 bonus pillow</span>
              )}
            </div>

            {/* HEIGHT */}
            <div className="mt-6">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#6b6b72] mb-2">
                Height
              </div>
              <div className="toggle-pill">
                {HEIGHTS.map((h) => (
                  <button
                    key={h.key}
                    data-testid={`height-${h.key.toLowerCase()}`}
                    className={height === h.key ? "active" : ""}
                    onClick={() => updateHeight(h.key)}
                  >
                    {h.label}{" "}
                    <span className="opacity-70 text-[11px]">
                      {h.cm}
                      {h.key === "High" ? " · +$10/pillow" : ""}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* COLOR */}
            <div className="mt-5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#6b6b72] mb-2 flex justify-between">
                <span>Color</span>
                <span className="text-[#8a8a8a] normal-case tracking-normal font-normal">
                  {color}
                </span>
              </div>
              <div className="flex gap-2.5">
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    data-testid={`color-${c.key.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => updateColor(c.key)}
                    title={c.label}
                    aria-label={c.label}
                    className={`relative w-11 h-11 rounded-full border-2 transition-all duration-300 ease-out hover:scale-110 hover:shadow-md ${
                      color === c.key
                        ? "border-[#0e0e10] scale-110 ring-2 ring-[#d8f06b]/40 ring-offset-2 ring-offset-white"
                        : "border-black/10 hover:border-[#0e0e10]/60"
                    }`}
                    style={{ background: c.swatch }}
                  >
                    {color === c.key && (
                      <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#0e0e10]" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* BUNDLE SELECTOR */}
            <div className="mt-6">
              <div className="text-[11px] font-bold uppercase tracking-wider text-[#6b6b72] mb-2 flex justify-between">
                <span>How many?</span>
                <span className="text-[#4a6cff]">More = more savings</span>
              </div>
              <div className="mb-3">
                <ShippingProgressBar qty={selected.qty} price={basePillowPrice} />
                <ShippingTrustBlock />
              </div>
              <div className="grid grid-cols-3 gap-2 items-end">
                {BUNDLES.map((b) => {
                  const tilePrice = b.price + (height === "High" ? 10 * b.qty : 0);
                  const perPillow = (tilePrice / b.qty).toFixed(0);
                  const isSuper = b.qty === 2;
                  return (
                    <button
                      key={b.qty}
                      data-testid={LANDING.bundleCard(b.qty)}
                      onClick={() => updateQty(b.qty)}
                      className={`bundle-tile relative ${
                        qty === b.qty ? "selected" : ""
                      } ${
                        isSuper
                          ? "ring-2 ring-[#d8f06b] shadow-lg -translate-y-1.5 md:-translate-y-2.5 scale-[1.04] z-10"
                          : ""
                      }`}
                    >
                      {b.badge && (
                        <span
                          className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[8px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider whitespace-nowrap ${
                            isSuper
                              ? "bg-[#d8f06b] text-[#0e0e10]"
                              : "bg-[#0e0e10] text-white"
                          }`}
                        >
                          {b.badge}
                        </span>
                      )}
                      <div className="flex items-end justify-center gap-0.5 h-[28px]">
                        {Array.from({ length: b.qty }).map((_, i) => (
                          <span
                            key={i}
                            aria-hidden
                            className={`inline-block rounded-sm ${
                              qty === b.qty ? "bg-[#0e0e10]" : "bg-[#0e0e10]/60"
                            }`}
                            style={{
                              width: b.qty === 4 ? "5px" : b.qty === 2 ? "8px" : "12px",
                              height: b.qty === 4 ? "16px" : b.qty === 2 ? "20px" : "24px",
                            }}
                          />
                        ))}
                      </div>
                      <div className="text-[20px] font-black text-[#0e0e10] leading-none mt-1.5">
                        {b.qty}
                      </div>
                      <div className="text-[9px] uppercase tracking-wider text-[#8a8a8a] mt-0.5">
                        {b.qty === 1 ? "Pillow" : "Pillows"}
                      </div>
                      <div className="mt-2 flex items-baseline justify-center gap-0.5">
                        <span className="text-[18px] font-black text-[#0e0e10] tabular-nums leading-none">
                          ${perPillow}
                        </span>
                        <span className="text-[9px] font-bold text-[#5a5a60] uppercase tracking-wider">
                          /ea
                        </span>
                      </div>
                      <div className="text-[10px] text-[#6b6b72] tabular-nums mt-0.5">
                        ${tilePrice} total
                      </div>
                      {b.savings > 0 && (
                        <div className="text-[10px] text-[#22c55e] font-extrabold mt-0.5">
                          −${b.savings}
                        </div>
                      )}
                      {GIFT_MASKS_BY_BUNDLE[b.qty] > 0 && (
                        <div
                          data-testid={`bundle-gift-badge-${b.qty}`}
                          className="mt-1.5 text-[9px] uppercase tracking-wider font-extrabold text-[#0e0e10] bg-[#d8f06b] rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5"
                          title="Free 3D sleep mask"
                        >
                          🎁 +{GIFT_MASKS_BY_BUNDLE[b.qty]} MASK
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* HEIGHT UPSELL NUDGE (Medium → High) */}
            {height === "Medium" && (
              <button
                type="button"
                onClick={() => updateHeight("High")}
                data-testid="height-upsell-nudge"
                className="mt-4 w-full text-left rounded-2xl border border-[#0e0e10]/15 bg-gradient-to-r from-[#d8f06b]/30 to-[#bfd8ff]/30 px-4 py-3 hover:border-[#0e0e10] transition group"
              >
                <div className="flex items-start gap-3">
                  <span className="text-[18px] leading-none mt-0.5">🔥</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-bold text-[#0e0e10] leading-snug">
                      87% of side sleepers prefer the High loft.
                    </div>
                    <div className="text-[11px] text-[#3a3a40] mt-0.5">
                      Upgrade to 18 cm for only{" "}
                      <strong className="text-[#0e0e10]">
                        +${10 * selected.qty}
                      </strong>{" "}
                      ({selected.qty} × $10) — more cervical support.
                    </div>
                  </div>
                  <span className="text-[11px] font-extrabold uppercase tracking-wider text-[#0e0e10] whitespace-nowrap group-hover:underline">
                    Upgrade →
                  </span>
                </div>
              </button>
            )}

            <LaunchPromoExplainer qty={qty} />

            {/* FREE GIFT — 3D sleep mask (1 for bundle=2, 2 for bundle=4) */}
            <GiftMaskSelector
              count={giftMaskCount}
              selectedColors={maskColors}
              onChange={updateMaskColor}
            />

            {/* BUMP ORDER — +1 extra pillow for $69 (saves $78 vs $147) */}
            <button
              type="button"
              onClick={() => setBumpExtra((v) => !v)}
              data-testid="bump-order-toggle"
              aria-pressed={bumpExtra}
              className={`mt-4 w-full text-left rounded-2xl p-4 transition-all border-2 ${
                bumpExtra
                  ? "border-[#22c55e] bg-gradient-to-r from-[#dcfce7] to-[#d8f06b]/40 shadow-[0_4px_20px_rgba(34,197,94,0.18)]"
                  : "border-dashed border-[#0e0e10]/25 bg-[#fffaf0] hover:border-[#0e0e10] hover:bg-[#fff5dc]"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`shrink-0 mt-0.5 w-6 h-6 rounded-md border-2 inline-flex items-center justify-center transition ${
                    bumpExtra
                      ? "bg-[#22c55e] border-[#22c55e] text-white"
                      : "bg-white border-[#0e0e10]/40"
                  }`}
                  aria-hidden
                >
                  {bumpExtra && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-extrabold text-[#0e0e10] bg-[#d8f06b] px-2 py-0.5 rounded-full">
                      🎁 One-time add-on
                    </span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-[#22c55e]">
                      Save $78
                    </span>
                  </div>
                  <div className="mt-1.5 text-[13px] md:text-[14px] font-black text-[#0e0e10] leading-snug">
                    YES! Add 1 extra Dubai Pillow for only{" "}
                    <span className="tabular-nums">+${bumpCharge || 69}</span>
                    <span className="text-[#8a8a8a] line-through font-bold ml-1.5 tabular-nums">$147</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#3a3a40] leading-snug">
                    Same {color.toLowerCase()} · {height.toLowerCase()} loft · ships together
                    {height === "High" && " (incl. +$10 high loft)"}
                  </div>
                </div>
              </div>
            </button>

            {/* URGENCY */}
            <div className="mt-5 flex items-center gap-2 text-[12px] text-[#6b6b72]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444] pulse-dot" />
              Only 23 left at this price · 18 viewing now
            </div>

            <div className="mt-4">
              <ReviewsRatingStrip />
            </div>

            {/* ORDER BREAKDOWN — transparency before checkout */}
            <div
              data-testid="shop-order-breakdown"
              className="mt-5 rounded-2xl bg-[#fafaf9] border border-black/5 p-4 text-[12px]"
            >
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold text-[#8a8a8a] mb-2.5">
                Your order
              </div>
              <div className="flex justify-between py-1">
                <span className="text-[#3a3a40]">
                  {selected.qty}× Dubai Pillow
                  <span className="text-[#8a8a8a]"> · {height} loft · {color}</span>
                </span>
                <span className="font-bold text-[#0e0e10] tabular-nums">${basePillowPrice}</span>
              </div>
              {bumpExtra && (
                <div className="flex justify-between py-1 text-[#22c55e]">
                  <span>+ 1× Bonus Pillow <span className="text-[#3a3a40]/70">(same height & color)</span></span>
                  <span className="font-bold tabular-nums">+${bumpCharge}</span>
                </div>
              )}
              {giftMaskCount > 0 && (
                <div className="flex justify-between py-1 text-[#22c55e]">
                  <span>🎁 + {giftMaskCount}× 3D Sleep Mask{giftMaskCount > 1 ? "s" : ""} <span className="text-[#3a3a40]/70">({maskColors.slice(0, giftMaskCount).join(", ")})</span></span>
                  <span className="font-bold">FREE</span>
                </div>
              )}
              {hasDiscount && (
                <div className="flex justify-between py-1 text-[#22c55e]">
                  <span>{discountCode} — {Math.round(couponPct * 100)}% off</span>
                  <span className="font-bold tabular-nums">−${(displayedPrice - finalPrice).toFixed(2)}</span>
                </div>
              )}

              {/* COUPON CODE INPUT */}
              <div className="border-t border-black/5 my-2.5" />
              {hasDiscount ? (
                <div
                  data-testid="coupon-applied"
                  className="flex items-center justify-between gap-2 rounded-xl bg-[#22c55e]/8 border border-[#22c55e]/25 px-3 py-2.5"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="shrink-0 inline-flex w-6 h-6 items-center justify-center rounded-full bg-[#22c55e] text-white">
                      <Check size={13} strokeWidth={3} />
                    </span>
                    <div className="min-w-0">
                      <div className="text-[12px] font-extrabold text-[#0e0e10] tracking-wide truncate">
                        {discountCode}
                      </div>
                      <div className="text-[10px] text-[#3a3a40]">
                        {Math.round(VALID_COUPONS[discountCode] * 100)}% off · saving ${(displayedPrice - finalPrice).toFixed(2)}
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    data-testid="coupon-remove"
                    aria-label="Remove coupon"
                    className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#0e0e10]/60 hover:text-[#0e0e10] transition"
                  >
                    <X size={12} /> Remove
                  </button>
                </div>
              ) : (
                <div data-testid="coupon-block">
                  <label className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-extrabold text-[#8a8a8a] mb-1.5">
                    <Tag size={11} /> Coupon code
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      data-testid="coupon-input"
                      value={couponInput}
                      onChange={(e) => {
                        setCouponInput(e.target.value.toUpperCase());
                        if (couponError) setCouponError("");
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyCoupon();
                        }
                      }}
                      placeholder="Enter your Coupon"
                      autoComplete="off"
                      spellCheck="false"
                      className={`flex-1 min-w-0 rounded-full bg-white border px-3.5 py-2 text-[12px] tracking-wider font-semibold uppercase placeholder:normal-case placeholder:font-normal placeholder:tracking-normal placeholder:text-[#a1a1aa] focus:outline-none focus:ring-2 focus:ring-[#0e0e10]/15 transition ${
                        couponError ? "border-[#ef4444]" : "border-black/10"
                      }`}
                    />
                    <button
                      type="button"
                      onClick={applyCoupon}
                      data-testid="coupon-apply"
                      disabled={!couponInput.trim()}
                      className="shrink-0 rounded-full bg-[#0e0e10] text-white px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.12em] hover:bg-[#3a3a40] disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                      Apply
                    </button>
                  </div>
                  {couponError && (
                    <div
                      data-testid="coupon-error"
                      className="mt-1.5 text-[11px] text-[#ef4444] font-semibold"
                    >
                      {couponError}
                    </div>
                  )}
                </div>
              )}

              <div className="border-t border-black/5 my-2" />
              <div className="flex justify-between text-[13px]">
                <span className="font-bold text-[#0e0e10]">Total</span>
                <span className="font-black text-[#0e0e10] tabular-nums">
                  ${hasDiscount ? finalPrice.toFixed(2) : displayedPrice.toFixed(2)} USD
                </span>
              </div>
              <div className="text-[10px] text-[#8a8a8a] mt-1">
                Free worldwide shipping · 100-night sleep trial included
              </div>
            </div>

            {/* CTA */}
            <button
              data-testid={LANDING.addToCart}
              disabled={loading}
              onClick={checkout}
              className="pill-btn w-full mt-5 py-4 text-[14px] disabled:opacity-60"
            >
              {loading
                ? "One moment…"
                : hasDiscount
                ? `Make ${totalPillows > 1 ? "them" : "it"} mine — $${finalPrice.toFixed(2)} →`
                : `Make ${totalPillows > 1 ? "them" : "it"} mine — $${displayedPrice} →`}
            </button>

            {hasDiscount && (
              <div className="mt-2 text-[12px] text-[#22c55e] text-center font-semibold">
                ✓ {discountCode} applied · You save an extra ${(displayedPrice - finalPrice).toFixed(2)} at checkout
              </div>
            )}
            {discountCode && !hasDiscount && (
              <div className="mt-2 text-[12px] text-[#22c55e] text-center">
                ✓ Code {discountCode} will be applied at checkout
              </div>
            )}

            <div className="mt-5 grid grid-cols-2 gap-2 text-[11px] text-[#6b6b72]">
              <div className="flex items-center gap-1.5">
                <Shield size={13} /> Secure checkout
              </div>
              <div className="flex items-center gap-1.5">
                <Globe size={13} /> Free worldwide shipping
              </div>
              <div className="flex items-center gap-1.5">
                <RotateCcw size={13} /> 30-night returns
              </div>
              <div className="flex items-center gap-1.5">
                <Leaf size={13} /> Ethically sourced
              </div>
            </div>

            <div className="mt-4">
              <TrustBadgesStrip />
            </div>
          </div>
        </div>
      </div>

      <PreCheckoutEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onContinue={continueToStripe}
        summary={{
          qty,
          totalPillows,
          displayedPrice,
          finalPrice,
          height,
          color,
          hasDiscount,
        }}
      />
    </section>
  );
}
